/* ════════════════════════════════════════════════════════════════════════
   Birthday Intelligence — derived Family → Planning context.
   Birthdays live ONCE on the Family Profile (member.birthday, free text).
   Planning derives occurrences; nothing is persisted or duplicated.
   Feb 29 policy: in non-leap years the occurrence falls on 28 February.
   ════════════════════════════════════════════════════════════════════════ */

const MONTHS = ["january","february","march","april","may","june","july","august","september","october","november","december"];

/* Parse free-text birthdays: "14 September", "14 September 1978", "Sep 14",
   "14/9", "14/09/1978", "2014-09-14". Returns {day, month(1-12), year|null}. */
export function parseBirthday(text) {
  const t = String(text || "").trim().toLowerCase();
  if (!t) return null;

  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return valid(+m[3], +m[2], +m[1]);

  m = t.match(/^(\d{1,2})[\/.](\d{1,2})(?:[\/.](\d{2,4}))?$/);
  if (m) return valid(+m[1], +m[2], m[3] ? normYear(+m[3]) : null);

  m = t.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+(?:of\s+)?([a-z]+)\.?,?\s*(\d{4})?$/);
  if (m) {
    const month = monthIndex(m[2]);
    if (month) return valid(+m[1], month, m[3] ? +m[3] : null);
  }

  m = t.match(/^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s*(\d{4})?$/);
  if (m) {
    const month = monthIndex(m[1]);
    if (month) return valid(+m[2], month, m[3] ? +m[3] : null);
  }
  return null;
}

function monthIndex(word) {
  const idx = MONTHS.findIndex(mo => mo.startsWith(word.slice(0, 3)));
  return idx === -1 ? null : idx + 1;
}
function normYear(y) { return y < 100 ? (y > 26 ? 1900 + y : 2000 + y) : y; }
function valid(day, month, year) {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { day, month, year: year || null };
}

const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

/* Next annual occurrence relative to `today` (Date). Feb 29 → Feb 28 in
   non-leap years. Never mutates the stored date of birth. */
export function nextBirthdayOccurrence(parsed, today = new Date()) {
  const base = new Date(today); base.setHours(0, 0, 0, 0);
  const occurrenceIn = (year) => {
    let { day, month } = parsed;
    if (month === 2 && day === 29 && !isLeap(year)) day = 28;
    return new Date(year, month - 1, day);
  };
  let occ = occurrenceIn(base.getFullYear());
  if (occ < base) occ = occurrenceIn(base.getFullYear() + 1);
  return occ;
}

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/* Derived, household-scoped, chronological. Retains member identity. */
export function getUpcomingFamilyBirthdays(members, today = new Date()) {
  const base = new Date(today); base.setHours(0, 0, 0, 0);
  return (members || [])
    .map(m => {
      const parsed = parseBirthday(m.birthday);
      if (!parsed) return null;
      const date = nextBirthdayOccurrence(parsed, base);
      const daysUntil = Math.round((date - base) / 86400000);
      const cel = m.celebrations || {};
      const firstName = (m.preferredName || m.name || "").trim().split(" ")[0];
      return {
        memberId: m.id,
        memberName: firstName,
        date,
        iso: toISO(date),
        daysUntil,
        /* Never expose birth year in Planning. */
        displayDate: date.toLocaleDateString("en-NZ", { day: "numeric", month: "long" }),
        weekday: date.toLocaleDateString("en-NZ", { weekday: "long" }),
        giftIdeas: cel.giftIdeas || [],
        wants: cel.wants || [],
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.daysUntil - b.daysUntil);
}

/* Calm relative phrasing — never urgent. */
export function birthdayRelative(daysUntil) {
  if (daysUntil === 0) return "today";
  if (daysUntil === 1) return "tomorrow";
  if (daysUntil < 7) return "this week";
  const weeks = Math.round(daysUntil / 7);
  if (weeks === 1) return "next week";
  if (daysUntil <= 56) return `in ${["","one","two","three","four","five","six","seven","eight"][weeks] || weeks} weeks`;
  return "later this year";
}

/* One thoughtful sentence for the Planning hero — or null if there is
   nothing useful to say (>6 weeks away stays quiet). */
export function birthdayFocalSentence(upcoming) {
  const b = (upcoming || []).find(x => x.daysUntil <= 42);
  if (!b) return null;
  if (b.daysUntil === 0) return `Happy birthday, ${b.memberName}.`;
  if (b.daysUntil === 1) return `${b.memberName}'s birthday is tomorrow.`;
  if (b.daysUntil < 7) return `${b.memberName}'s birthday falls on ${b.weekday}.`;
  if (b.daysUntil <= 21) {
    const ideas = b.giftIdeas.length;
    return ideas > 0
      ? `${b.memberName}'s birthday is ${birthdayRelative(b.daysUntil)} — you've saved ${ideas} gift idea${ideas === 1 ? "" : "s"}.`
      : `${b.memberName}'s birthday is ${birthdayRelative(b.daysUntil)}.`;
  }
  return `${b.memberName}'s birthday is coming up on ${b.displayDate}.`;
}
